import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  StatusBadge,
  formatCurrency,
  formatDate,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import type { Invoice } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

export function Billing() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const checkout = params.get("checkout");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const [sub, inv, ents, cfg] = await Promise.all([
        api.subscription(orgId),
        api.invoices(orgId),
        api.entitlements(orgId),
        api.billingConfig(),
      ]);
      return {
        subscription: sub.subscription,
        invoices: inv.invoices,
        entitlements: ents.entitlements,
        liveCheckout: cfg.liveCheckout,
      };
    },
    [orgId],
  );

  async function openPortal() {
    setBusy(true);
    setNotice(null);
    try {
      const { url } = await api.openBillingPortal(orgId);
      window.location.assign(url);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not open the billing portal.");
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<unknown>, msg: string) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice(msg);
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  const sub = state.data?.subscription;

  const invoiceCols = [
    { key: "number", header: "Invoice", render: (r: Invoice) => r.number },
    { key: "created", header: "Date", render: (r: Invoice) => formatDate(r.created_at) },
    { key: "total", header: "Amount", render: (r: Invoice) => formatCurrency(r.total, r.currency) },
    { key: "status", header: "Status", render: (r: Invoice) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Billing</h1>
          <p>Manage your subscription, seats, usage, and invoices.</p>
        </div>
        <Link to="/studio/usage">
          <Button variant="secondary" size="sm">
            View usage
          </Button>
        </Link>
      </div>

      {welcome && <div className="banner banner--info">🎉 Your subscription is active. Welcome to Contract AI!</div>}
      {checkout === "success" && (
        <div className="banner banner--info">
          ✅ Payment received. We&apos;re activating your subscription from Stripe&apos;s confirmation. Entitlements appear here once the payment is verified.
        </div>
      )}
      {checkout === "cancelled" && (
        <div className="banner banner--warning">Checkout was cancelled. No charge was made.</div>
      )}
      {notice && <div className="banner banner--info">{notice}</div>}

      {state.loading && <LoadingState />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}

      {state.data && (
        <div className="two-col">
          <div className="ods-stack">
            <Card>
              <CardHeader title="Current plan" action={sub ? <StatusBadge status={sub.status} /> : undefined} />
              <CardBody>
                {!sub ? (
                  <EmptyState
                    icon="💳"
                    title="No active subscription"
                    description="Choose a plan to enable Contract AI for your organization."
                    action={
                      <Link to="/pricing">
                        <Button>View plans</Button>
                      </Link>
                    }
                  />
                ) : (
                  <>
                    <div className="ods-row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                      <div>
                        <div style={{ fontSize: "var(--ods-text-2xl)", fontWeight: 800 }}>{sub.plan_name}</div>
                        <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{sub.plan_tier} tier</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700 }}>
                          {sub.seats} seat{sub.seats === 1 ? "" : "s"}
                        </div>
                        <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{sub.assigned_seats} assigned</span>
                      </div>
                    </div>
                    <dl className="kv" style={{ marginTop: "var(--ods-space-4)" }}>
                      <dt>Current period</dt>
                      <dd>
                        {formatDate(sub.current_period_start)} to {formatDate(sub.current_period_end)}
                      </dd>
                      {sub.trial_ends_at && (
                        <>
                          <dt>Trial ends</dt>
                          <dd>{formatDate(sub.trial_ends_at)}</dd>
                        </>
                      )}
                      <dt>Renewal</dt>
                      <dd>{sub.cancel_at_period_end ? "Cancels at period end" : "Renews automatically"}</dd>
                    </dl>
                    {sub.trial_ends_at && new Date(sub.trial_ends_at).getTime() < Date.now() && (
                      <div className="banner banner--warning" style={{ marginTop: "var(--ods-space-4)" }}>
                        Your trial has expired. Add a plan to keep access.
                      </div>
                    )}
                    <div className="ods-row" style={{ marginTop: "var(--ods-space-4)", flexWrap: "wrap" }}>
                      <Link to="/studio/seats">
                        <Button variant="secondary" size="sm">
                          Manage seats
                        </Button>
                      </Link>
                      {sub.cancel_at_period_end ? (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => act(() => api.resumeSubscription(orgId, sub.id), "Subscription resumed.")}
                        >
                          Resume subscription
                        </Button>
                      ) : (
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={busy}
                          onClick={() => act(() => api.cancelSubscription(orgId, sub.id), "Subscription set to cancel at period end.")}
                        >
                          Cancel subscription
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Invoice history" />
              <CardBody>
                {state.data.invoices.length === 0 ? (
                  <EmptyState icon="🧾" title="No invoices yet" description="Invoices will appear here after your first billing cycle." />
                ) : (
                  <DataTable columns={invoiceCols} rows={state.data.invoices} caption="Invoice history" />
                )}
              </CardBody>
            </Card>
          </div>

          <div className="ods-stack">
            <Card>
              <CardHeader title="Payment method" />
              <CardBody>
                <div className="banner banner--info" style={{ marginBottom: 0 }}>
                  🔒 Payments are handled by Stripe. Manage cards and receipts in the provider portal. No card data
                  is stored by Odingard.
                </div>
                <Button
                  variant="secondary"
                  block
                  style={{ marginTop: "var(--ods-space-4)" }}
                  disabled={!sub || busy || !state.data?.liveCheckout}
                  onClick={openPortal}
                >
                  Open payment portal
                </Button>
                {!state.data?.liveCheckout && (
                  <p style={{ fontSize: "var(--ods-text-xs)", color: "var(--ods-color-muted)", marginTop: "var(--ods-space-2)" }}>
                    The live provider portal is available once Stripe is enabled for this environment.
                  </p>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Entitlements" />
              <CardBody>
                {state.data.entitlements.length === 0 ? (
                  <EmptyState icon="🔑" title="No entitlements" />
                ) : (
                  <div className="ods-stack">
                    {state.data.entitlements.map((e) => (
                      <div key={e.key} className="ods-row" style={{ justifyContent: "space-between" }}>
                        <code style={{ fontSize: "var(--ods-text-sm)" }}>{e.key}</code>
                        <Badge tone={["ALLOWED", "TRIAL"].includes(e.status) ? "success" : "danger"}>{e.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
